/**
 * Gallery data module — ported from Studio, using workspace store/eventBus/config
 * Exports: loadGallery, getTabState, toggleFavorite, loadServerFavorites
 * Self-registers with router as 'gallery' mode
 */

import { CONFIG, getCdnThumbnail } from '../../core/config.js'
import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { registerMode, setMode } from '../../core/router.js'
import { useImageAsSource } from '../create-ui/image-roles.js'
import { getActiveSubtab, registerSubtabs, setSubtab } from '../../core/subtabs.js'
import { showToast } from '../../utils/toast.js'
import { apiRequest, fetchImageDetail, fetchWithTimeout, getSessionId, uploadImage } from '../../api/index.js'
import { createLogger } from '../../utils/logger.js'

const log = createLogger('gallery')

// ---- L2 subtabs (H-2026-09-12-135a) ----

// The waterfall's view tabs move from the in-grid header to the standard L2
// bar (task card §3). Labels are English — the app is EN-only (bao
// 2026-10-24). `getTabState` stays the single content resolver — the L2 id
// is mapped to the legacy galleryTab key.
export const GALLERY_SUBTABS = [
  { id: 'all', label: 'All', default: true },
  { id: 'favorites', label: 'Favorites' },
  { id: 'uploads', label: 'Uploads' },
  { id: 'mine', label: 'Mine' },
]
registerSubtabs('gallery', GALLERY_SUBTABS)

const SUBTAB_TO_GALLERY_TAB = {
  all: 'gallery',
  favorites: 'favorites',
  uploads: 'uploads',
  mine: 'mine',
}

/** Pure: L2 subtab id → the galleryTab state key getTabState resolves on. */
export function subtabToGalleryTab(subtabId) {
  return SUBTAB_TO_GALLERY_TAB[subtabId] || 'gallery'
}

let galleryLoadPromise = null

// Concurrent consumers must share the boot preload; returning early while it is
// active leaves picker callers with no completion signal and an empty modal.
export function loadGallery(append = false) {
  if (galleryLoadPromise) return galleryLoadPromise

  galleryLoadPromise = loadGalleryPage(append).finally(() => {
    galleryLoadPromise = null
  })
  return galleryLoadPromise
}

async function loadGalleryPage(append) {
  const gallery = store.getState('gallery')
  store.setState('gallery.loading', true)
  const page = append ? gallery.page + 1 : 1
  const offset = (page - 1) * CONFIG.GALLERY_PAGE_SIZE
  const limit = CONFIG.GALLERY_PAGE_SIZE

  try {
    const sessionId = getSessionId()
    const headers = {}
    const token = store.getState('auth.token')

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    } else {
      headers['X-Session-ID'] = sessionId
    }

    // Gallery uses the canonical creator-api curated image feed. It reads the
    // IMAGES_DB library and excludes user uploads with unclear provenance.
    const url = `${CONFIG.FRONTEND_API}/api/images?scene=gallery&limit=${limit}&offset=${offset}&session_id=${sessionId}`
    const res = await fetchWithTimeout(url, { headers })
    const data = await res.json()

    // Creator image feed envelope: { success, images: [...], has_more }.
    const items = data.images || data.recommendations
    if (data.success && Array.isArray(items)) {
      const imgs = items.map(normalizeImage)
      const currentImages = store.getState('gallery.images')
      store.setState('gallery.images', append ? currentImages.concat(imgs) : imgs)
      store.setState('gallery.page', page)
      store.setState('gallery.total', data.total || imgs.length)
      store.setState('gallery.hasMore', data.has_more !== false && imgs.length >= limit)
    }
  } catch (e) {
    log.error('Failed to load gallery:', e)
    showToast('Failed to load gallery', 'error')
  } finally {
    store.setState('gallery.loading', false)
  }
}

export function normalizeImage(img) {
  const displayName = img.title || img.filename || 'Image'
  // 2026-09-14 prod incident (H-145): R2-backed rows (H-139+) carry no
  // cloudflare_id, and their DB row id is NOT a CF image id — the previous
  // `cloudflare_id || img.id` fabrication fed a row id into getCdnThumbnail,
  // which built an imagedelivery variant URL that CF serves as text/plain +
  // nosniff and Chrome ORB-blocks (whole gallery wall "Failed to load").
  // The ADR-006-era claim that url/thumbnail_url point at the retired
  // images-api host no longer holds for these rows: those fields are live
  // R2 CDN URLs. So: real cloudflare_id -> CF Images variant; otherwise use
  // the stored R2 URLs as-is. getCdnThumbnail already encodes both states
  // (H-141), reuse it directly.
  const thumbnailUrl = getCdnThumbnail(img)
  const fullUrl = img.cloudflare_id
    ? getCdnThumbnail(img, 1200)
    : img.url || img.main_image_url
  return {
    id: img.id || img.cloudflare_id,
    cloudflare_id: img.cloudflare_id || null,
    url: fullUrl,
    thumbnail: thumbnailUrl,
    title: displayName,
    filename: displayName,
    width: img.width,
    height: img.height,
    style: img.style || null,
    uploadedAt: img.created_at || img.uploaded_at,
  }
}

// Get tab state (for gallery tabs: gallery/favorites/uploads/mine)
export function getTabState() {
  const tab = store.getState('galleryTab') || 'gallery'
  const favoriteIds = store.getState('favoriteIds')

  if (tab === 'favorites') {
    const allImages = store.getState('gallery.images')
    const favoriteImages = allImages.filter((img) => favoriteIds.has(String(img.id)))
    return {
      images: favoriteImages,
      total: favoriteImages.length,
      hasMore: false,
      loading: false,
    }
  }

  if (tab === 'uploads') {
    const uploads = store.getState('uploads')
    return {
      images: uploads.images || [],
      total: uploads.total || 0,
      hasMore: uploads.hasMore || false,
      loading: uploads.loading || false,
    }
  }

  if (tab === 'mine') {
    const mine = store.getState('mine')
    return {
      images: mine.images || [],
      total: mine.total || 0,
      hasMore: false,
      loading: mine.loading || false,
    }
  }

  // Default: gallery tab
  const gallery = store.getState('gallery')
  return {
    images: gallery.images,
    total: gallery.total,
    hasMore: gallery.hasMore,
    loading: gallery.loading,
  }
}

// Toggle favorite
export function toggleFavorite(imageId) {
  // Normalize to string: library img.id arrives as a JSON number, but the server
  // stores item_id as TEXT and returns it as a string. The Set must key on
  // strings on BOTH the write and the has()/heart-state side or server-loaded
  // favorites never match displayed images. why → code review HIGH.
  const id = String(imageId)
  const favoriteIds = store.getState('favoriteIds')
  const isNowFav = !favoriteIds.has(id)
  if (isNowFav) {
    favoriteIds.add(id)
  } else {
    favoriteIds.delete(id)
  }

  // Force store notification (Set is mutated in-place)
  store.setState('favoriteIds', favoriteIds)

  // Persist to localStorage
  localStorage.setItem('mms_favorites', JSON.stringify([...favoriteIds]))

  // Emit event for UI updates
  eventBus.emit(isNowFav ? EVENTS.FAVORITE_ADD : EVENTS.FAVORITE_REMOVE, imageId)

  // Sync to backend if logged in. Unified onto the customer favorites endpoint
  // (CUSTOMER_API/api/favorites, item_type='image') — the old
  // FRONTEND_API/api/user/favorites path was never implemented on creator-api.
  // why → one favorites system (customer-favorites, item_type-keyed).
  const customer = store.getState('auth.customer')
  const token = store.getState('auth.token')
  if (customer?.id && token) {
    const base = `${CONFIG.CUSTOMER_API}/api/favorites`
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    if (isNowFav) {
      fetchWithTimeout(base, {
        method: 'POST',
        headers,
        body: JSON.stringify({ item_type: 'image', item_id: String(imageId) }),
      }).catch(() => {})
    } else {
      const q = `item_type=image&item_id=${encodeURIComponent(String(imageId))}`
      fetchWithTimeout(`${base}?${q}`, { method: 'DELETE', headers }).catch(() => {})
    }
  }
}

// Rehydrate favorites from the localStorage cache (anon / offline). The cache
// was previously write-only — read it back so a returning user's hearts light
// before/without a server round-trip. String-normalized like the rest. why →
// code review MEDIUM (write-only localStorage).
export function hydrateLocalFavorites() {
  try {
    const raw = localStorage.getItem('mms_favorites')
    if (!raw) return
    const ids = JSON.parse(raw)
    if (!Array.isArray(ids)) return
    const favoriteIds = store.getState('favoriteIds')
    ids.forEach((id) => favoriteIds.add(String(id)))
    store.setState('favoriteIds', favoriteIds)
  } catch {
    /* ignore corrupt cache */
  }
}

export async function loadServerFavorites() {
  const token = store.getState('auth.token')
  if (!token) return
  try {
    // Image favorites only (item_type='image') from the unified customer
    // favorites endpoint. why → one favorites system, see toggle above.
    const res = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/favorites?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const data = await res.json()
    const favoriteIds = store.getState('favoriteIds')
    const serverIds = (data.data || [])
      .filter((f) => f.item_type === 'image')
      .map((f) => f.item_id)
      .filter(Boolean)
    serverIds.forEach((id) => favoriteIds.add(String(id)))
    store.setState('favoriteIds', favoriteIds)
    localStorage.setItem('mms_favorites', JSON.stringify([...favoriteIds]))
  } catch {
    /* silent */
  }
}

// ---- Mine tab (H-135a) ----

// The "mine" L2 tab shows the signed-in customer's own generated assets
// (create-runtime GET /api/customer/assets?purpose=ai_generation). Guests get
// the empty state — asset listing is auth-scoped by design.
export async function loadMineCreations() {
  if (!store.getState('auth.token')) return
  store.setState('mine.loading', true)
  try {
    const data = await apiRequest(
      `${CONFIG.FRONTEND_API}/api/customer/assets?purpose=ai_generation&limit=50`
    )
    // public_url stays null until compliance approves the asset (ADR-008
    // fail-closed gate) — unapprovable rows would render as broken thumbs.
    const images = (data.data || [])
      .filter((a) => a?.public_url)
      .map((a) => ({
        id: a.id,
        cloudflare_id: null,
        url: a.public_url,
        thumbnail: a.public_url,
        title: a.title || 'Creation',
        filename: a.title || 'Creation',
        width: a.width,
        height: a.height,
        uploadedAt: a.created_at,
      }))
    store.setState('mine', { images, total: images.length, loading: false, hasMore: false })
  } catch (e) {
    log.error('Failed to load mine creations:', e)
    store.setState('mine.loading', false)
  }
}

// ---- Page actions ----

// One layout across modes (bao 2026-10-24): stage actions live in the
// shared in-stage toolbar row (.mode-toolbar, the Create topbar pattern),
// not the frame header. Uploaded assets land in the Uploads tab — the
// curated gallery feed excludes user uploads by design (see loadGalleryPage).
function ensureGalleryActions() {
  const btn = document.getElementById('galleryUploadBtn')
  if (btn && !btn.dataset.bound) {
    btn.dataset.bound = '1'
    btn.addEventListener('click', pickGalleryUpload)
  }
}

function pickGalleryUpload() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.addEventListener('change', async () => {
    const file = input.files?.[0]
    if (!file) return
    try {
      const fd = new FormData()
      fd.append('image', file)
      const asset = await uploadImage(fd)
      const url = asset?.public_url || asset?.url
      if (!url) throw new Error('upload returned no asset')
      // Prepend so the Uploads tab renders the new asset immediately.
      const uploads = store.getState('uploads')
      uploads.images = [
        { id: asset.id ?? url, cloudflare_id: null, url, thumbnail: url, title: file.name, filename: file.name },
        ...(uploads.images || []),
      ]
      uploads.total = (uploads.total || 0) + 1
      store.setState('uploads', uploads)
      // Jump to the Uploads L2 tab — setSubtab emits SUBTAB_CHANGE, which the
      // gallery wiring below maps to galleryTab + a re-render.
      setSubtab('gallery', 'uploads')
      waterfallRenderer?.renderGallery()
      showToast('Upload complete', 'success')
    } catch (err) {
      log.error('gallery upload failed:', err)
      showToast('Upload failed — please try again', 'error')
    }
  })
  input.click()
}

// ---- Mode lifecycle ----

let waterfallRenderer = null
let galleryPick = null
let renderSubscribed = false

/**
 * Mirror the active L2 subtab into the legacy galleryTab key and lazily load
 * the mine feed on first entry. Called on gallery activate and on every
 * gallery SUBTAB_CHANGE.
 */
function syncGalleryTabFromSubtab() {
  const subtab = getActiveSubtab('gallery')
  store.setState('galleryTab', subtabToGalleryTab(subtab))
  if (subtab === 'mine' && !store.getState('mine.images').length) {
    loadMineCreations().then(() => waterfallRenderer?.renderGallery())
  }
}

// L2 tab switches drive the waterfall: the subtab store is written by
// core/subtabs (bar click or URL), here we map it onto the content resolver.
eventBus.on(EVENTS.SUBTAB_CHANGE, (mode, tabId) => {
  if (mode !== 'gallery') return
  store.setState('galleryTab', subtabToGalleryTab(tabId))
  if (tabId === 'mine' && !store.getState('mine.images').length) {
    loadMineCreations().then(() => waterfallRenderer?.renderGallery())
  }
  waterfallRenderer?.renderGallery()
})

async function activateGallery() {
  const container = document.getElementById('mode-gallery')
  if (!container) return

  // Lazy-load waterfall renderer
  if (!waterfallRenderer) {
    const mod = await import('../create-ui/gallery-waterfall.js')
    waterfallRenderer = mod
  }

  // Set up gallery grid container if not present. The view tabs moved to the
  // standard L2 bar (H-135a) — no more in-grid tab header; the image count
  // lives in the L2 bar aside slot (re-created by the waterfall renderer).
  // Stage actions ride the shared .mode-toolbar row (one layout across modes).
  if (!container.querySelector('#galleryGrid')) {
    container.innerHTML = `
      <div class="mode-toolbar">
        <div class="mode-toolbar__left"></div>
        <div class="mode-toolbar__right">
          <button type="button" id="galleryUploadBtn" class="pf-action-btn">
            <i class="ph ph-upload-simple" aria-hidden="true"></i><span>Upload</span>
          </button>
        </div>
      </div>
      <div class="ws-gallery__grid" id="galleryGrid"></div>
      <!-- Fixed generate bar (2026-10-27, bao): pinned bottom — click a
           material to select it, the generate action stays put. -->
      <div class="ws-gallery__genbar" id="galleryGenBar">
        <span class="ws-gallery__genbar-hint sg-empty" data-genbar-hint>Pick a material</span>
        <button type="button" class="ws-gallery__genbar-go" data-genbar-go disabled>Generate 9</button>
      </div>`
  }

  // Selection → the fixed generate bar mirrors the pick.
  waterfallRenderer.setGallerySelectHandler?.((img) => {
    galleryPick = img
    const hint = container.querySelector('[data-genbar-hint]')
    const go = container.querySelector('[data-genbar-go]')
    if (hint) {
      hint.textContent = img?.title || `Material #${img?.id}`
      hint.classList.remove('sg-empty')
    }
    if (go) go.disabled = !img
  })
  const genGo = container.querySelector('[data-genbar-go]')
  if (genGo && !genGo.dataset.wired) {
    genGo.dataset.wired = '1'
    genGo.addEventListener('click', () => {
      if (!galleryPick?.url) return
      const pick = galleryPick
      setMode('create')
      useImageAsSource(pick, 'library')
      // Dispatch the batch once the studio bar exists (paid action — only
      // from this explicit Generate click).
      const tryDispatch = (n) => {
        const btn = document.querySelector('[data-studio-action="generate"]')
        if (btn && !btn.disabled) {
          btn.click()
          return
        }
        if (n > 0) setTimeout(() => tryDispatch(n - 1), 120)
      }
      tryDispatch(20)
    })
  }

  // The L2 subtab is the source of truth (deep link ?mode=gallery&tab=X was
  // applied to the store before this activate). Mirror it into galleryTab —
  // the key getTabState resolves on.
  syncGalleryTabFromSubtab()

  // Unified PageHeader actions slot: Upload entry (H-130).
  ensureGalleryActions()

  // Hydrate favorites: local cache first (instant, covers anon/offline), then
  // server (authoritative for logged-in). Both String-normalize ids. why →
  // code review HIGH+MEDIUM (server read was dead, localStorage was write-only).
  hydrateLocalFavorites()
  await loadServerFavorites()

  // Load gallery data if empty
  const gallery = store.getState('gallery')
  if (!gallery.images.length) {
    await loadGallery()
  }

  // Render waterfall
  waterfallRenderer.renderGallery()

  // Re-render whenever the image set changes while gallery is the active
  // mode — the boot preload (main.js loadGallery) races the router's
  // first activate, and without this the deep-link/first-visit view stuck
  // at "0 images" until a manual tab click. why → journey verification.
  if (!renderSubscribed) {
    renderSubscribed = true
    store.subscribe('gallery.images', () => {
      if (store.getState('mode') === 'gallery') waterfallRenderer?.renderGallery()
    })
  }
}

function deactivateGallery() {
  // Disconnect any infinite scroll observer
  // (handled internally by waterfall renderer)
}

// Self-register with router
registerMode('gallery', { activate: activateGallery, deactivate: deactivateGallery })

// Invalidate gallery cache when a new image is generated so it appears on next visit
eventBus.on(EVENTS.GENERATION_COMPLETE, () => {
  // Reset gallery state so next activate does a fresh load
  store.setState('gallery.images', [])
  store.setState('gallery.page', 1)
  store.setState('gallery.hasMore', true)
  // The mine feed holds generated assets too — drop it so the next visit to
  // the "mine" tab refetches (it would otherwise serve a pre-generation list).
  store.setState('mine', { images: [], total: 0, loading: false, hasMore: false })
})
